/* eslint-disable */
import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import Select from "react-select";
import { useTranslation } from "react-i18next";
import { postData, getData } from "utils/Gateway";
import * as url from "helper/UrlHelper";
import TaskPriority from "data/Prod/TaskPriority.json";

const AddMultipleTaskModal = ({ handleClose, reloadData }) => {
  const { t } = useTranslation();
  const token = localStorage.getItem("token");
  const userInfo = JSON.parse(localStorage.getItem("userInfo"));

  // Default task state
  const defaultTaskState = [
    {
      title: "",
      project: null,
      startdate: "",
      enddate: "",
      hours: 1,
      priority: "2",
      rank: 1,
    },
  ];

  const [tasks, setTasks] = useState(defaultTaskState);
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [projectSearchQuery, setProjectSearchQuery] = useState("");

  // Create a ref array to store references to title input fields
  const titleInputRefs = useRef([]);

  // Format priority options for the dropdown
  const priorityOptions = TaskPriority.map((priority) => ({
    value: priority.value,
    label: priority.label,
  }));

  // Custom styles for react-select
  const customSelectStyles = {
    control: (provided) => ({
      ...provided,
      borderColor: "#ced4da",
      "&:hover": {
        borderColor: "#ced4da",
      },
      borderRadius: "7px",
      padding: "4.5px",
      minHeight: "40px",
      boxShadow: "none",
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
  };

  // Fetch projects based on search
  const searchProjects = async (query) => {
    if (!query || query.length < 2) {
      setProjects([]);
      return;
    }

    try {
      setIsLoading(true);
      const requestUrl =
        url.API_BASE_URL +
        url.API_GET_PROJECTLIST_BY_SEARCH +
        `?token=${token}&searchtitle=${query}`;
      const response = await getData(requestUrl);

      if (response.status && response.data) {
        const formattedProjects = response.data.map((project) => ({
          value: project._id,
          label: project.label,
        }));
        setProjects(formattedProjects);
      } else {
        setProjects([]);
      }
      setIsLoading(false);
    } catch (error) {
      console.error("Error searching projects:", error);
      setProjects([]);
      setIsLoading(false);
    }
  };

  // Handle input change for project search
  const handleProjectInputChange = (inputValue) => {
    setProjectSearchQuery(inputValue);
    if (inputValue && inputValue.length >= 2) {
      searchProjects(inputValue);
    }
  };

  const handleInputChange = (index, field, value) => {
    const updatedTasks = [...tasks];
    updatedTasks[index][field] = value;
    setTasks(updatedTasks);
  };

  const handleDeleteRow = (index) => {
    const updatedTasks = tasks.filter((_, i) => i !== index);
    setTasks(updatedTasks);
  };

  const handleAddRow = () => {
    const newTasks = [
      ...tasks,
      {
        title: "",
        project: null,
        startdate: "",
        enddate: "",
        hours: 1,
        priority: "2",
        rank: tasks.length + 1,
      },
    ];
    setTasks(newTasks);

    // Focus on the title input of the newly added row after render
    setTimeout(() => {
      const newIndex = newTasks.length - 1;
      if (titleInputRefs.current[newIndex]) {
        titleInputRefs.current[newIndex].focus();
      }
    }, 0);
  };

  const handleKeyPress = (index, field, event) => {
    if (field === "rank" && event.key === "Enter") {
      handleAddRow();
    }
  };

  const handleCloseAndReset = () => {
    setTasks(defaultTaskState);
    setErrorMessage("");
  };

  // Update refs when tasks array changes
  useEffect(() => {
    titleInputRefs.current = titleInputRefs.current.slice(0, tasks.length);
  }, [tasks.length]);

  const handleSave = async () => {
    // Validate required fields
    const isValid = tasks.every(
      (task) =>
        task.title.trim() && task.project && task.startdate && task.enddate
    );

    if (!isValid) {
      setErrorMessage("Please fill in all required fields for all tasks");
      return;
    }

    const tasksData = {
      tasks: tasks.map((task) => ({
        title: task.title,
        projectid: task.project.value,
        startdate: task.startdate,
        enddate: task.enddate,
        hours: task.hours,
        priority: task.priority,
        rank: task.rank,
        moderator: userInfo._id,
      })),
    };

    try {
      setIsLoading(true);

      const requestUrl =
        url.API_BASE_URL + url.API_SAVE_QUICK_TASKS + `?token=${token}`;
      const response = await postData(requestUrl, tasksData);

      setIsLoading(false);

      if (response.status) {
        // Close the modal
        const bootstrapModal = document.querySelector("#addMultipleTaskModal");
        const modal = bootstrap.Modal.getInstance(bootstrapModal);
        modal.hide();
        setTasks(defaultTaskState);
        handleCloseAndReset();
        handleClose(response.data);
      } else {
        setErrorMessage(response.message || "Failed to save tasks");
      }
    } catch (error) {
      setIsLoading(false);
      setErrorMessage("Error saving tasks. Please try again.");
      console.error("Error saving tasks:", error);
    }
  };

  return (
    <div
      className="modal fade"
      id="addMultipleTaskModal"
      tabIndex="-1"
      aria-labelledby="addMultipleTaskModalLabel"
      aria-hidden="true"
      data-bs-backdrop="static"
      data-bs-keyboard="false"
    >
      <div className="modal-dialog modal-xl" style={{ maxWidth: "90vw" }}>
        <div className="modal-content bg-white border-0 rounded-15 shadow-lg">
          {/* Enhanced Header with Icon and Gradient Background */}
          <div className="modal-header bg-primary text-white px-4 py-3 border-bottom border-gray-300">
            <div className="d-flex align-items-center gap-3">
              <div className="icon-wrapper p-3 bg-white rounded-circle d-flex align-items-center justify-content-center">
                <span className="material-symbols-outlined text-primary fs-5 icon-md">
                  add_task
                </span>
              </div>
              <div>
                <h3
                  className="fw-bold fs-2 text-white mb-0"
                  id="addMultipleTaskModalLabel"
                >
                  {t("Add Multiple Tasks")}
                </h3>
                <p className="text-white fs-sm mb-0">
                  {t("Add more than one task at once using this feature.")}
                </p>
              </div>
            </div>
            <button
              type="button"
              className="btn-close p-2 bg-gray-200 rounded-circle shadow-sm border-0"
              data-bs-dismiss="modal"
              aria-label="Close"
              onClick={handleCloseAndReset}
            ></button>
          </div>
          <div className="modal-body p-4">
            <div className="p-3 p-md-4 border border-gray-300 rounded-10 shadow-sm">
              <form onSubmit={(e) => e.preventDefault()}>
                {errorMessage && (
                  <div className="alert alert-danger mt-2" role="alert">
                    {errorMessage}
                  </div>
                )}
                <div className="table-wrapper">
                  <div className="table-responsive mb-1">
                    <table
                      className="table align-middle fs-sm text-nowrap border-0 mb-0"
                      style={{ height: "200px" }}
                    >
                      <thead>
                        <tr>
                          <th
                            width="15%"
                            className="bg-transparent fs-md fw-semibold border-0"
                          >
                            {t("Task")}
                          </th>
                          <th
                            width="25%"
                            className="bg-transparent fs-md fw-semibold border-0"
                          >
                            {t("Project")}
                          </th>
                          <th
                            width="10%"
                            className="bg-transparent fs-md fw-semibold border-0"
                          >
                            {t("Start Date")}
                          </th>
                          <th
                            width="10%"
                            className="bg-transparent fs-md fw-semibold border-0"
                          >
                            {t("End Date")}
                          </th>
                          <th
                            width="5%"
                            className="bg-transparent fs-md fw-semibold border-0"
                          >
                            {t("Hours")}
                          </th>
                          <th
                            width="20%"
                            className="bg-transparent fs-md fw-semibold border-0"
                          >
                            {t("Priority")}
                          </th>
                          <th
                            width="5%"
                            className="bg-transparent fs-md fw-semibold border-0"
                          >
                            {t("Rank")}
                          </th>
                          <th className="bg-transparent fs-md fw-semibold border-0">
                            {t("Action")}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {tasks.map((task, index) => (
                          <tr key={index} style={{ verticalAlign: "top" }}>
                            <td className="border-bottom-0 p-1">
                              <textarea
                                className="form-control bg-white shadow-none border-gray-300"
                                style={{
                                  width: "100%",
                                  minWidth: "250px",
                                  minHeight: "40px",
                                }}
                                placeholder={t("Enter task description")}
                                value={task.title}
                                ref={(el) =>
                                  (titleInputRefs.current[index] = el)
                                }
                                onChange={(e) =>
                                  handleInputChange(
                                    index,
                                    "title",
                                    e.target.value
                                  )
                                }
                              />
                            </td>
                            <td className="border-bottom-0 p-1">
                              <Select
                                className="basic-single"
                                classNamePrefix="select"
                                value={task.project}
                                onChange={(value) =>
                                  handleInputChange(index, "project", value)
                                }
                                options={projects}
                                onInputChange={handleProjectInputChange}
                                isClearable={true}
                                isSearchable={true}
                                placeholder={t("Search project...")}
                                noOptionsMessage={() =>
                                  projectSearchQuery.length < 2
                                    ? t("Type at least 2 characters to search")
                                    : t("No options")
                                }
                                styles={customSelectStyles}
                                filterOption={() => true}
                                isLoading={isLoading}
                              />
                            </td>
                            <td className="border-bottom-0 p-1">
                              <input
                                type="date"
                                className="form-control bg-white shadow-none border-gray-300"
                                value={task.startdate}
                                onChange={(e) =>
                                  handleInputChange(
                                    index,
                                    "startdate",
                                    e.target.value
                                  )
                                }
                              />
                            </td>
                            <td className="border-bottom-0 p-1">
                              <input
                                type="date"
                                className="form-control bg-white shadow-none border-gray-300"
                                value={task.enddate}
                                onChange={(e) =>
                                  handleInputChange(
                                    index,
                                    "enddate",
                                    e.target.value
                                  )
                                }
                              />
                            </td>
                            <td className="border-bottom-0 p-1">
                              <input
                                type="number"
                                className="form-control bg-white shadow-none border-gray-300"
                                min="0"
                                step="0.5"
                                value={task.hours}
                                onChange={(e) =>
                                  handleInputChange(
                                    index,
                                    "hours",
                                    parseFloat(e.target.value) || 0
                                  )
                                }
                              />
                            </td>
                            <td className="border-bottom-0 p-1">
                              <select
                                className="form-select bg-white shadow-none border-gray-300"
                                value={task.priority}
                                onChange={(e) =>
                                  handleInputChange(
                                    index,
                                    "priority",
                                    e.target.value
                                  )
                                }
                              >
                                {priorityOptions.map((option) => (
                                  <option
                                    key={option.value}
                                    value={option.value}
                                  >
                                    {option.label}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td className="border-bottom-0 p-1">
                              <input
                                type="number"
                                className="form-control bg-white shadow-none border-gray-300"
                                min="1"
                                value={task.rank}
                                onChange={(e) =>
                                  handleInputChange(
                                    index,
                                    "rank",
                                    parseInt(e.target.value) || 1
                                  )
                                }
                                onKeyPress={(e) =>
                                  handleKeyPress(index, "rank", e)
                                }
                              />
                            </td>
                            <td className="action border-bottom-0">
                              <Link
                                to="#"
                                className="text-danger"
                                onClick={() => handleDeleteRow(index)}
                              >
                                <span className="d-block material-symbols-outlined">
                                  delete
                                </span>
                              </Link>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <button
                    className="btn btn-outline-primary mt-2"
                    onClick={handleAddRow}
                  >
                    + {t("Add New")}
                  </button>
                </div>
                <div className="action d-flex justify-content-end pt-3 border-top mt-4">
                  <button
                    onClick={handleSave}
                    type="button"
                    className="btn btn-primary d-flex align-items-center gap-2"
                    disabled={isLoading}
                    style={{
                      cursor: isLoading ? "not-allowed" : "pointer",
                    }}
                  >
                    {isLoading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        {t("Saving...")}
                      </>
                    ) : (
                      <>
                        <span className="material-symbols-outlined me-1">
                          save
                        </span>{" "}
                        {t("Save Tasks")}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddMultipleTaskModal;
